/**
 * QuoteToolbarActions.tsx
 *
 * The rest of legacy's quote toolbar — Presets, Find Plan, Portal, Excel and
 * History — as a row above the section bands.
 *
 * Each opens a small panel rather than a nested modal, so a coordinator never
 * has two dialogs stacked over a quote they are mid-edit on.
 *
 * Portal only appears when the patient actually has portal access, matching
 * legacy: the button is meaningless otherwise, and an admin, not the person
 * reading this, is the one who grants it.
 *
 * Find Plan follows legacy (encounter-details-ds2.php:559-571): it is offered
 * only on a quote not yet on a plan, with the Plans module on, and the choice
 * is held in the editor's working copy until Save stores it
 * (quote-handling-ds.js:5363-5378). A locked quote shows none of Presets,
 * Excel or Find Plan (encounter-details-ds2.php:542-573). History stays, as
 * legacy loads its History panel on every quote (encounter-details-ds2.php:
 * 1176-1190).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, History, Link2, MonitorSmartphone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { listMerchants } from '@/services/payments.service';
import {
  applyQuotePreset,
  downloadQuoteExcel,
  getQuoteHistory,
  getQuotePlanOptions,
  getQuotePortalStatus,
  getQuotePresets,
  publishQuoteToPortal,
  unpublishQuoteFromPortal,
  type QuoteHistoryEntry,
  type QuotePlanOption,
  type QuotePreset,
} from '@/services/quotes.service';

type Panel = 'presets' | 'plan' | 'portal' | 'history' | null;

/** A plan picked in Find Plan and not saved yet. `displayId` is the number staff see. */
export interface StagedQuotePlan {
  planId: number;
  displayId: number;
}

interface Props {
  quoteId: number;
  /** The plan the saved quote is on; 0 for none. */
  planId: number;
  readOnly: boolean;
  /** Whether the practice has the Plans module. */
  plansEnabled: boolean;
  /** The plan Find Plan picked, waiting for Save. */
  stagedPlan: StagedQuotePlan | null;
  onStagePlan: (planId: number, displayId: number) => void;
  /** Whether the quote already has rows a preset would replace. */
  hasContent: boolean;
  /** Re-read the quote after an action changes its contents. */
  onChanged: () => void;
}

export function QuoteToolbarActions({
  quoteId, planId, readOnly, plansEnabled, stagedPlan, onStagePlan, hasContent, onChanged,
}: Props) {
  const queryClient = useQueryClient();
  const [panel, setPanel] = useState<Panel>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const portal = useQuery({
    queryKey: ['quote-portal', quoteId],
    queryFn: () => getQuotePortalStatus(quoteId),
  });

  const toggle = (next: Panel) => {
    setError(null);
    setPanel((current) => (current === next ? null : next));
  };

  async function downloadExcel() {
    setBusy(true);
    setError(null);
    try {
      const blob = await downloadQuoteExcel(quoteId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quoteId}.xlsx`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not build the spreadsheet for this quote. Try again. If it keeps happening, contact Prospera support.'));
    } finally {
      setBusy(false);
    }
  }

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['quote-portal', quoteId] });
    onChanged();
  };

  const canFindPlan = !readOnly && plansEnabled && planId === 0;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {!readOnly && (
          <Button size="sm" variant="secondary" onClick={() => toggle('presets')} leftIcon={<Sparkles className="h-3.5 w-3.5" />}>
            Presets
          </Button>
        )}
        {canFindPlan && (
          <Button size="sm" variant="secondary" onClick={() => toggle('plan')} leftIcon={<Link2 className="h-3.5 w-3.5" />}>
            Find Plan
          </Button>
        )}
        {!readOnly && (
          <Button size="sm" variant="secondary" onClick={downloadExcel} loading={busy} leftIcon={<FileSpreadsheet className="h-3.5 w-3.5" />}>
            Excel
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={() => toggle('history')} leftIcon={<History className="h-3.5 w-3.5" />}>
          History
        </Button>
        {portal.data?.available ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => toggle('portal')}
            leftIcon={<MonitorSmartphone className="h-3.5 w-3.5" />}
          >
            Portal{portal.data.published ? ' — shared' : ''}
          </Button>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {canFindPlan && stagedPlan ? (
        <p className="text-xs text-slate-500">
          Plan # {stagedPlan.displayId} will be added to this quote when you save.
        </p>
      ) : null}

      {panel === 'presets' ? (
        <PresetsPanel quoteId={quoteId} hasContent={hasContent} onDone={() => { setPanel(null); onChanged(); }} onError={setError} />
      ) : null}

      {panel === 'plan' && canFindPlan ? (
        <PlanPanel
          quoteId={quoteId}
          stagedPlanId={stagedPlan?.planId ?? null}
          onPick={(pickedId, displayId) => { setPanel(null); onStagePlan(pickedId, displayId); }}
        />
      ) : null}

      {panel === 'portal' ? (
        <PortalPanel quoteId={quoteId} published={portal.data?.published ?? false} onDone={refreshAll} onError={setError} />
      ) : null}

      {panel === 'history' ? <HistoryPanel quoteId={quoteId} /> : null}
    </div>
  );
}

const panelClass = 'rounded-md border border-slate-200 bg-slate-50 p-3';

/**
 * Legacy's Load Preset replaces the quote's procedures, products and discounts
 * with the preset's (quote_load_template.php:28-213), so a quote that already
 * has rows is asked about first.
 */
function PresetsPanel({
  quoteId, hasContent, onDone, onError,
}: { quoteId: number; hasContent: boolean; onDone: () => void; onError: (m: string) => void }) {
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [presetId, setPresetId] = useState<number | ''>('');
  const [confirmReplace, setConfirmReplace] = useState(false);

  const { data } = useQuery({ queryKey: ['quote-presets'], queryFn: getQuotePresets, staleTime: 300_000 });

  const apply = useMutation({
    mutationFn: () => applyQuotePreset(quoteId, presetId as number),
    onSuccess: onDone,
    onError: (err) => onError(getErrorMessage(err, 'Could not load that preset onto this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  const presets = (data?.presets ?? []).filter(
    (p: QuotePreset) => categoryId === '' || p.categoryId === categoryId,
  );

  return (
    <div className={panelClass}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">
          Preset Category
          <select
            className="block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            value={categoryId}
            onChange={(e) => { setCategoryId(e.target.value ? Number(e.target.value) : ''); setPresetId(''); }}
          >
            <option value="">All categories</option>
            {(data?.categories ?? []).map((c) => (
              <option key={c.categoryId} value={c.categoryId}>{c.name}</option>
            ))}
          </select>
        </label>

        <label className="text-xs text-slate-600">
          Preset
          <select
            className="block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            value={presetId}
            onChange={(e) => setPresetId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select one…</option>
            {presets.map((p) => (
              <option key={p.presetId} value={p.presetId}>{p.name}</option>
            ))}
          </select>
        </label>

        <Button
          size="sm"
          disabled={presetId === ''}
          loading={apply.isPending}
          onClick={() => (hasContent ? setConfirmReplace(true) : apply.mutate())}
        >
          Load Preset
        </Button>
      </div>
      <p className="mt-2 text-xs text-slate-500">
        A preset replaces this quote’s procedures, products and discounts with its own.
      </p>

      <ConfirmDialog
        isOpen={confirmReplace}
        onClose={() => setConfirmReplace(false)}
        onConfirm={() => {
          setConfirmReplace(false);
          apply.mutate();
        }}
        title="Replace this quote’s contents?"
        message="The procedures, products and discounts on this quote will be removed and the preset’s put in their place."
        confirmLabel="Replace"
        variant="warning"
      />
    </div>
  );
}

/** The number staff see for a plan: its display number, else its id. */
function planNumber(p: QuotePlanOption): number {
  return p.displayId > 0 ? p.displayId : p.planId;
}

/**
 * Picks a plan for the quote. Nothing is stored here: the pick goes into the
 * editor's working copy and Save stores it, as legacy's Find Plan only fills
 * the form's planID. There is no "no plan" choice, because legacy has no way
 * to take a quote off a plan.
 */
function PlanPanel({
  quoteId, stagedPlanId, onPick,
}: { quoteId: number; stagedPlanId: number | null; onPick: (planId: number, displayId: number) => void }) {
  const [selected, setSelected] = useState<number | ''>(stagedPlanId ?? '');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['quote-plan-options', quoteId],
    queryFn: () => getQuotePlanOptions(quoteId),
  });

  const options = data ?? [];
  const chosen = options.find((p: QuotePlanOption) => p.planId === selected) ?? null;

  if (isError) {
    return (
      <div className={panelClass}>
        <p className="text-sm text-red-700">
          Could not load this patient’s plans. Close Find Plan and open it again. If it keeps
          happening, contact Prospera support.
        </p>
      </div>
    );
  }

  if (!isLoading && options.length === 0) {
    return <div className={panelClass}><p className="text-sm italic text-slate-400">This patient has no plans yet.</p></div>;
  }

  return (
    <div className={panelClass}>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">
          Plan
          <select
            className="block rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            value={selected}
            onChange={(e) => setSelected(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Select one…</option>
            {options.map((p: QuotePlanOption) => (
              <option key={p.planId} value={p.planId}>
                {`Plan ${planNumber(p)}`}
              </option>
            ))}
          </select>
        </label>

        <Button
          size="sm"
          disabled={chosen === null}
          onClick={() => { if (chosen) onPick(chosen.planId, planNumber(chosen)); }}
        >
          Use this plan
        </Button>
      </div>
    </div>
  );
}

function PortalPanel({
  quoteId, published, onDone, onError,
}: { quoteId: number; published: boolean; onDone: () => void; onError: (m: string) => void }) {
  // Which merchant the patient pays through. Reuses the payments area's list
  // rather than introducing a second one.
  const { data: merchants, isLoading: merchantsLoading, isError: merchantsFailed } = useQuery({
    queryKey: ['payment-merchants'],
    queryFn: listMerchants,
    enabled: !published,
    staleTime: 300_000,
    retry: false,
  });

  const [merchantId, setMerchantId] = useState<number | null>(null);
  const chosen = merchantId ?? merchants?.find((m) => m.isDefault)?.merchantId ?? merchants?.[0]?.merchantId ?? null;

  /**
   * A practice with no payment account cannot share a quote — the portal needs
   * one to take payment against. That is a setup task, not something the person
   * in front of this screen can fix, so say so instead of leaving a disabled
   * button with no options and no explanation.
   *
   * Covers a practice that has never set one up, and one whose plan does not
   * include payments at all (that list is behind its own module, so the request
   * fails rather than returning an empty list).
   */
  const noMerchants = !published && !merchantsLoading && (merchantsFailed || (merchants ?? []).length === 0);

  const publish = useMutation({
    mutationFn: () => publishQuoteToPortal(quoteId, chosen as number),
    onSuccess: onDone,
    onError: (err) => onError(getErrorMessage(err, 'Could not share this quote with the patient. Try again. If it keeps happening, contact Prospera support.')),
  });

  const unpublish = useMutation({
    mutationFn: () => unpublishQuoteFromPortal(quoteId),
    onSuccess: onDone,
    onError: (err) => onError(getErrorMessage(err, 'Could not stop sharing this quote. Try again. If it keeps happening, contact Prospera support.')),
  });

  return (
    <div className={panelClass}>
      {published ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-700">This quote is visible to the patient in their portal.</p>
          <Button size="sm" variant="secondary" loading={unpublish.isPending} onClick={() => unpublish.mutate()}>
            Stop sharing
          </Button>
        </div>
      ) : noMerchants ? (
        <p className="text-sm text-slate-700">
          Sharing a quote needs a payment account, so the patient can pay it. Ask your
          administrator to set one up, then share the quote again.
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-700">Share this quote with the patient in their portal.</p>
          <div className="flex items-center gap-2 max-md:flex-wrap">
            <label className="text-xs text-slate-600">
              Patient pays through
              <select
                className="ml-2 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                value={chosen ?? ''}
                onChange={(e) => setMerchantId(e.target.value ? Number(e.target.value) : null)}
              >
                {(merchants ?? []).map((m) => (
                  <option key={m.merchantId} value={m.merchantId}>{m.friendlyTitle}</option>
                ))}
              </select>
            </label>
            <Button
              size="sm"
              loading={publish.isPending}
              disabled={chosen === null}
              onClick={() => publish.mutate()}
            >
              Share
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ quoteId }: { quoteId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['quote-history', quoteId],
    queryFn: () => getQuoteHistory(quoteId),
  });

  if (isLoading) {
    return <div className={panelClass}><p className="text-sm text-slate-500">Loading history…</p></div>;
  }

  if (!data || data.length === 0) {
    return <div className={panelClass}><p className="text-sm italic text-slate-400">No saved versions yet.</p></div>;
  }

  return (
    <div className={panelClass}>
      <div className="max-md:overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-1 pr-4">Saved</th>
              <th className="py-1 pr-4">By</th>
              <th className="py-1 pr-4">Contents</th>
              <th className="py-1 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.map((entry: QuoteHistoryEntry) => (
              <tr key={entry.quoteHistoryId} className="border-t border-slate-200">
                <td className="py-1 pr-4 whitespace-nowrap">{formatDate(entry.savedAt)}</td>
                <td className="py-1 pr-4">{entry.savedBy ?? '—'}</td>
                <td className="py-1 pr-4 text-slate-500">
                  {entry.counts.procedures} procedures, {entry.counts.items} products
                </td>
                <td className="py-1 text-right">{formatCurrency(entry.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
